/**
 * Trading Profiles API — Supabase PostgREST queries.
 *
 * The `trading_profiles` row is unique per user and holds the user's
 * "Open To" exchange preferences (and legacy offer/seek tags). It is the
 * source of truth for these preferences so they survive reload and follow
 * the user across devices.
 */

import supabase from "../config/supabase";

const tradingProfilesApi = {
  /**
   * Fetch the current user's trading profile (or a specific user's).
   * Returns { success, data } where data may be null if none exists yet.
   */
  async get(userId) {
    let resolvedUserId = userId;
    if (!resolvedUserId) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { success: false, error: "Not authenticated" };
      resolvedUserId = user.id;
    }

    const { data, error } = await supabase
      .from("trading_profiles")
      .select("*")
      .eq("user_id", resolvedUserId)
      .maybeSingle();

    if (error) return { success: false, error: error.message };
    return { success: true, data: data || null };
  },

  /**
   * Persist the "Open To" exchange preferences for the current user.
   * Upserts on the unique user_id so it creates the row on first save and
   * updates it thereafter.
   */
  async saveExchangePreferences(preferences) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const safePrefs = Array.isArray(preferences) && preferences.length
      ? preferences
      : ["payment"];

    const { data, error } = await supabase
      .from("trading_profiles")
      .upsert(
        { user_id: user.id, exchange_preferences: safePrefs },
        { onConflict: "user_id" }
      )
      .select()
      .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data };
  },
};

export default tradingProfilesApi;
